import React from "react";
import { Box, CircularProgress, IconButton, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { GstPercentage } from "../../../../../models/Order/OrderStatus";

export type PrInlineFieldInputKind = "text" | "number" | "gst";

interface PrInlineEditableFieldProps {
  label: string;
  displayValue: React.ReactNode;
  canEdit: boolean;
  isEditing: boolean;
  isSaving: boolean;
  draft: string;
  error?: string;
  inputKind?: PrInlineFieldInputKind;
  disabledActions?: boolean;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDraftChange: (value: string) => void;
}

const PrInlineEditableField: React.FC<PrInlineEditableFieldProps> = ({
  label,
  displayValue,
  canEdit,
  isEditing,
  isSaving,
  draft,
  error,
  inputKind = "text",
  disabledActions = false,
  onStartEdit,
  onCancel,
  onSave,
  onDraftChange,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5} minHeight={20}>
        <CustomTypography variant="body3" color="text.secondary">
          {label}
        </CustomTypography>
        {canEdit && !isEditing && (
          <IconButton
            size="small"
            disabled={disabledActions || isSaving}
            onClick={onStartEdit}
            sx={{
              p: 0.35,
              color: colors.greyTextColor,
              "&:hover": {
                backgroundColor: "#EEF2FF",
                color: colors.purpleMain,
              },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>

      {isEditing ? (
        <Box mt={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            {inputKind === "gst" ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AppDropDown
                  isMultiSelect={false}
                  title=""
                  list={GstPercentage.map((gst) => ({
                    id: String(gst),
                    value: `${gst}%`,
                  }))}
                  value={draft}
                  disabled={isSaving}
                  fontSize="12px"
                  onSingleSelectionChange={(id: string) => {
                    onDraftChange(id);
                  }}
                />
              </Box>
            ) : (
              <CustomTextField
                width="100%"
                type={inputKind === "number" ? "number" : "text"}
                allowNegativeValue={false}
                allowDecimalValue={inputKind === "number"}
                value={draft}
                disabled={isSaving}
                fontSize="0.8rem"
                borderRadius="6px"
                onChange={(e) => onDraftChange(e.target.value)}
              />
            )}
            <IconButton
              size="small"
              disabled={isSaving}
              onClick={onCancel}
              sx={{ color: colors.greyTextColor }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={isSaving}
              onClick={onSave}
              sx={{
                color: colors.purpleMain,
                backgroundColor: "#EEF2FF",
                "&:hover": { backgroundColor: "#E0E7FF" },
              }}
            >
              {isSaving ? (
                <CircularProgress size={14} />
              ) : (
                <CheckIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
          <InlineFormError message={error} sx={{ mt: 0.5, mb: 0 }} />
        </Box>
      ) : (
        <>
          <CustomTypography
            variant="body1"
            fontWeight={600}
            sx={{ display: "block", mt: 0.5 }}
          >
            {displayValue}
          </CustomTypography>
          {error ? (
            <InlineFormError message={error} sx={{ mt: 0.5, mb: 0 }} />
          ) : null}
        </>
      )}
    </Box>
  );
};

export default PrInlineEditableField;
